import React from 'react';

import { Layout } from 'antd';
import { Link } from 'react-router';

import type { eq } from '@code-expert/prelude';
import { string } from '@code-expert/prelude';
import logoSvgInverted from '/assets/logo-inverted.svg';
import logoSvg from '/assets/logo.svg';
import { isProduction } from '/imports/config/client';
import { formatRoute } from '/imports/modules/Routing';
import { loginDemoRoute, loginRoute } from '/imports/startup/client/routes';
import { useGlobalContext } from '/imports/ui/components/GlobalContext';
import type { ItemType } from '/imports/ui/components/Menu';
import { Menu } from '/imports/ui/components/Menu';
import { ThemeSwitch } from '/imports/ui/components/ThemeSwitch';
import { Icon } from '/imports/ui/foundation/Icons';
import { css, important, isLight, styled, useTheme } from '/imports/ui/foundation/Theme';
import { getUserMenu } from '/imports/ui/layouts/getUserMenu';
import { adminRoute, adminSlug } from '/imports/ui/pages/admin/routes';
import { enroledRoute, enroledSlug } from '/imports/ui/pages/enrolled/routes';
import { myCoursesRoute, mycoursesSlug } from '/imports/ui/pages/myCourses/routes';
import { myExamsRoute, myexamsSlug } from '/imports/ui/pages/myExams/routes';
import { myGroupsRoute, mygroupsSlug } from '/imports/ui/pages/myGroups/routes';
import { printSlug } from '/imports/ui/pages/print/routes';

const StyledLayoutHeader = styled(Layout.Header, ({ tokens }) => ({
  '@media print': {
    display: 'none',
  },
  '&.ant-layout-header': {
    boxSizing: 'content-box',
  },
  padding: '0 !important',
  variants: {
    headerColor: {
      Black: { boxShadow: `inset 0 -2px 0 0 ${tokens.colorHeaderBlack}` },
      Red: { boxShadow: `inset 0 -2px 0 0 ${tokens.colorHeaderRed}` },
      Gold: { boxShadow: `inset 0 -2px 0 0 ${tokens.colorHeaderGold}` },
      Green: { boxShadow: `inset 0 -2px 0 0 ${tokens.colorHeaderGreen}` },
      Blue: { boxShadow: `inset 0 -2px 0 0 ${tokens.colorHeaderBlue}` },
      Magenta: { boxShadow: `inset 0 -2px 0 0 ${tokens.colorHeaderMagenta}` },
    },
  },
}));

const StyledMenu = styled(Menu, ({ tokens }) => ({
  background: 'none',
  borderBottom: 'none',
  fontSize: tokens.fontSizeLG,
  /* Increase the overflow menu item's click target */
  '.ant-menu-overflow-item-rest': {
    marginLeft: 'auto',
    paddingInline: 0,
    '& > *': {
      paddingInline: tokens.padding,
    },
  },
}));

/**
 * This targets the popup menu that is rendered in a React portal, so can't be targeted
 * by the menu's class hierarchy directly.
 */
const useMenuPopupStyles = css(() => ({
  '&.ant-menu-submenu': {
    '.ant-menu-item': {
      position: 'relative',
      ':is(a, button)::after': {
        /* Make the whole menu item a click target */
        position: 'absolute',
        inset: 0,
        zIndex: 1,
        content: "' '",
      },
    },
  },
}));

const useLogoStyles = css(() => ({
  display: 'grid !important',
  placeItems: 'center',
  width: 238,
  marginInlineStart: important('-2px'),
}));

const _topNavKeys = {
  none: null,
  app: null,
  [enroledSlug]: null,
  [mygroupsSlug]: null,
  [mycoursesSlug]: null,
  [myexamsSlug]: null,
  [adminSlug]: null,
  [printSlug]: null,
  login: null,
  cxUserMenu: null,
  themeSwitch: null,
};

type TopNavKey = keyof typeof _topNavKeys;

const topNavKeyEq: eq.Eq<TopNavKey> = string.Eq;

export function TopNavLayoutWrapper({
  menu,
  selected,
  children,
}: React.PropsWithChildren<{
  menu: ItemType<TopNavKey>[];
  selected: TopNavKey;
}>) {
  const { headerColor } = useGlobalContext();
  const { colorScheme } = useTheme();
  const popupClassName = useMenuPopupStyles();

  const logoClassName = useLogoStyles();
  const logoPath = isLight(colorScheme) ? logoSvg : logoSvgInverted;

  return (
    <Layout style={{ minHeight: '100%' }}>
      <StyledLayoutHeader headerColor={headerColor}>
        <StyledMenu
          mode="horizontal"
          rootClassName={popupClassName}
          overflowedIndicator={<Icon name="bars" />}
          selectedKeys={topNavKeyEq.equals(selected, 'none') ? [] : [selected]}
          triggerSubMenuAction="click"
          items={[
            {
              key: 'cxLayoutOverview',
              className: logoClassName,
              label: (
                <Link to="/">
                  <img src={logoPath} height="50" alt="Code Expert" />
                </Link>
              ),
            },
            ...menu,
          ]}
        />
      </StyledLayoutHeader>
      {children}
    </Layout>
  );
}

export function TopNavLayout({
  selected,
  disableLogin,
  children,
}: React.PropsWithChildren<{ disableLogin?: boolean; selected: TopNavKey }>) {
  const { user, userCapabilities, examMode } = useGlobalContext();

  return (
    <TopNavLayoutWrapper
      menu={[
        userCapabilities.includes('enrolled')
          ? {
              key: 'enrolled',
              label: <Link to={formatRoute(enroledRoute)({})}>Courses</Link>,
            }
          : null,
        userCapabilities.includes('groupAdmin')
          ? {
              key: 'mygroups',
              label: <Link to={formatRoute(myGroupsRoute)({})}>Exercise groups</Link>,
            }
          : null,
        userCapabilities.includes('courseAdmin')
          ? {
              key: 'mycourses',
              label: <Link to={formatRoute(myCoursesRoute)({})}>Course management</Link>,
            }
          : null,
        examMode && userCapabilities.includes('courseAdmin')
          ? {
              key: 'myexams',
              label: <Link to={formatRoute(myExamsRoute)({})}>Exams</Link>,
            }
          : null,
        userCapabilities.includes('admin')
          ? {
              key: 'admin',
              label: <Link to={formatRoute(adminRoute)({})}>Admin</Link>,
            }
          : null,
        user
          ? {
              ...getUserMenu(user),
              style: {
                marginLeft: 'auto',
              },
            }
          : null,
        !user && !disableLogin
          ? {
              key: 'login',
              label: (
                <Link to={formatRoute(isProduction ? loginRoute : loginDemoRoute)({})}>Login</Link>
              ),
              style: { marginLeft: 'auto' },
            }
          : null,
        {
          key: 'themeSwitch',
          label: <ThemeSwitch />,
        },
      ]}
      selected={selected}
    >
      {children}
    </TopNavLayoutWrapper>
  );
}
